import {
	type CommandConstructor,
	type CommandProperties,
	type SerializableValue,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

import type { ConfluenceAccumulatedDataType } from './confluence-accumulated-data-type';

/**
 * Data payload for updating Confluence slides.
 */
export type ConfluenceUpdateSlidesData = ConfluenceAccumulatedDataType & {
	/**
	 * The JSON document containing delta actions for the slides update.
	 */
	document: string;
	/**
	 * The content ID of the slides being updated.
	 */
	contentId: string;
};

/**
 * Properties for the Confluence slides update command.
 *
 * @extends CommandProperties
 */
export interface ConfluenceUpdateSlidesCommandProperties extends CommandProperties {
	/**
	 * The unique key identifier for the slides update request.
	 */
	key: string;
	/** Client submission identity, distinct from the stream request and group IDs. */
	editRequestId?: string;
	/** The action invocationId supplied by the AIFC stream. */
	editActionId?: string;
	/**
	 * The data payload containing the delta content for the slides update.
	 */
	data: ConfluenceUpdateSlidesData;
	/**
	 * Whether the stream is complete.
	 */
	isStreamComplete: boolean;
	/**
	 * Single instrumentation ID of the request
	 */
	singleInstrumentationID: string | undefined;
}

/**
 * A command sent by a bridge API client to update existing Confluence slides.
 *
 * @summary {@link CommandProperties.senderId} is requesting {@link CommandProperties.receiverId} to update Confluence slides with the specified delta content.
 */
export const ConfluenceSlidesUpdateCommand: CommandConstructor<
	ConfluenceUpdateSlidesCommandProperties,
	SerializableValue
> = createCommand<ConfluenceUpdateSlidesCommandProperties>('confluence-slides-update-command');
